import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Download, LogOut, Search, BookOpen, ListChecks } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { api, API } from "@/lib/api";

const SOURCE_LABELS = {
  instagram: "Instagram", youtube: "YouTube", tiktok: "TikTok",
  facebook: "Facebook", twitter: "X / Twitter", google: "Google",
  direct: "Doğrudan", other: "Diğer",
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!localStorage.getItem("life101_admin_token")) { nav("/admin/login"); return; }
    (async () => {
      try {
        const [s, l] = await Promise.all([api.get("/admin/stats"), api.get("/admin/submissions")]);
        setStats(s.data); setRows(l.data.rows);
      } catch (e) {
        if (e?.response?.status === 401 || e?.response?.status === 403) {
          const d = e?.response?.data?.detail;
          setErr(typeof d === "string" ? d : "Bu alana erişim yetkin yok.");
          setTimeout(() => nav("/admin/login"), 1200);
        }
      }
    })();
  }, [nav]);

  const logout = () => { localStorage.removeItem("life101_admin_token"); nav("/admin/login"); };

  const filtered = rows
    .filter((r) => (filter === "completed" ? r.status === "completed" : filter === "started" ? r.status !== "completed" : filter === "low" ? (r.general_score ?? 100) < 50 : true))
    .filter((r) => {
      if (!q.trim()) return true;
      const s = `${r.first_name} ${r.last_name} ${r.email}`.toLowerCase();
      return s.includes(q.toLowerCase());
    });

  const downloadCsv = async () => {
    const token = localStorage.getItem("life101_admin_token");
    const res = await fetch(`${API}/admin/export.csv`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "life101_export.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  if (err) return <div className="min-h-screen flex items-center justify-center text-neutral-300" style={{ background: "#050505" }}>{err}</div>;

  const sourceData = stats ? Object.entries(stats.sources || {})
    .map(([k, v]) => ({ name: SOURCE_LABELS[k] || k, value: v, key: k }))
    .filter((d) => d.value > 0) : [];
  const totalSources = sourceData.reduce((a, b) => a + b.value, 0);
  const COLORS = ["#ffffff", "#d4d4d4", "#a3a3a3", "#737373", "#525252", "#404040", "#262626", "#171717"];

  return (
    <div className="min-h-screen text-white" style={{ background: "#050505" }} data-testid="admin-dashboard">
      <header className="max-w-7xl mx-auto px-6 py-7 flex flex-wrap items-center justify-between gap-4">
        <div>
          <Link to="/admin" className="text-2xl font-bold">Life 101 Admin Paneli</Link>
          <p className="text-sm text-neutral-500 mt-1">Üyeleri, test cevaplarını, kaynakları ve soruları buradan yönet.</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Link to="/admin/questions" className="btn-secondary inline-flex items-center gap-2" data-testid="manage-questions-btn"><ListChecks size={16} /> Test Soruları</Link>
          <Link to="/admin/books" className="btn-secondary inline-flex items-center gap-2" data-testid="manage-books-btn"><BookOpen size={16} /> Kitap Yönetimi</Link>
          <Link to="/admin/resources" className="btn-secondary inline-flex items-center gap-2" data-testid="manage-resources-btn"><BookOpen size={16} /> Kaynak Yönetimi</Link>
          <button onClick={downloadCsv} className="btn-secondary inline-flex items-center gap-2" data-testid="csv-export-btn"><Download size={16} /> CSV İndir</button>
          <button onClick={logout} className="btn-secondary inline-flex items-center gap-2" data-testid="admin-logout"><LogOut size={16} /> Çıkış</button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            ["Toplam Üye", stats?.total_users ?? "—"],
            ["Testi Tamamlayan", stats?.completed ?? "—"],
            ["Ortalama Skor", stats?.avg_score ?? "—"],
            ["En Düşük Alan", stats?.lowest_area ?? "—"],
            ["Son 7 Gün", stats?.last_7_days ?? "—"],
          ].map(([label, val]) => (
            <div key={label} className="glass-solid p-5">
              <div className="text-xs uppercase tracking-[0.15em] text-neutral-500">{label}</div>
              <div className="mt-3 text-3xl font-semibold text-white">{val}</div>
            </div>
          ))}
        </div>

        {/* Traffic source chart */}
        <div className="glass-solid p-6" data-testid="sources-chart">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Trafik Kaynakları</h2>
            <span className="text-xs text-neutral-500">Toplam {totalSources} üye</span>
          </div>
          {sourceData.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={sourceData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} stroke="#000">
                    {sourceData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #262626", borderRadius: 8 }} />
                  <Legend wrapperStyle={{ color: "#a3a3a3", fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2">
                {sourceData.map((d, i) => (
                  <div key={d.key} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} /><span className="text-neutral-200">{d.name}</span></div>
                    <span className="text-neutral-500">{d.value} (%{((d.value / totalSources) * 100).toFixed(0)})</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-neutral-500 text-sm py-8 text-center">Henüz veri yok. Site linklerine <code className="text-neutral-300">?utm_source=instagram</code> gibi parametreler ekleyerek takip başlayabilir.</div>
          )}
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ad soyad veya e-posta ara..." className="bg-transparent border border-white/10 rounded-full pl-9 pr-4 py-2 text-sm text-white outline-none focus:border-white/30" data-testid="admin-search" />
          </div>
          {[["all", "Tümü"], ["completed", "Tamamlayanlar"], ["started", "Test Yapmamış"], ["low", "Düşük Skorlu"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} className={`px-4 py-2 rounded-full text-sm border ${filter === k ? "bg-white text-black border-white" : "border-white/10 text-neutral-300 hover:border-white/30"}`} data-testid={`filter-${k}`}>{l}</button>
          ))}
        </div>

        <div className="glass-solid overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-table">
            <thead>
              <tr className="text-left text-neutral-500 text-xs uppercase tracking-[0.12em]">
                <th className="p-4">Ad Soyad</th>
                <th className="p-4">E-posta</th>
                <th className="p-4">Kaynak</th>
                <th className="p-4">Durum</th>
                <th className="p-4">Skor</th>
                <th className="p-4">Düşük 3 Alan</th>
                <th className="p-4">Tarih</th>
                <th className="p-4 text-right">Detay</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.lead_id} className="border-t border-white/5">
                  <td className="p-4 text-neutral-100">{r.first_name} {r.last_name}</td>
                  <td className="p-4 text-neutral-300">{r.email}</td>
                  <td className="p-4 text-neutral-400">{SOURCE_LABELS[r.source] || r.source || "—"}</td>
                  <td className="p-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs border ${r.status === "completed" ? "border-white/30 text-white" : "border-white/10 text-neutral-400"}`}>
                      {r.status === "completed" ? "Tamamlandı" : r.status === "registered" ? "Üye" : "Yarım"}
                    </span>
                  </td>
                  <td className="p-4 text-neutral-100">{r.general_score ?? "—"}</td>
                  <td className="p-4 text-neutral-400">{(r.development_areas || []).join(", ") || "—"}</td>
                  <td className="p-4 text-neutral-500">{r.completed_at ? new Date(r.completed_at).toLocaleDateString("tr-TR") : new Date(r.created_at).toLocaleDateString("tr-TR")}</td>
                  <td className="p-4 text-right">
                    {r.submission_id ? (
                      <Link to={`/admin/submissions/${r.submission_id}`} className="text-white hover:underline" data-testid={`view-submission-${r.submission_id}`}>Görüntüle</Link>
                    ) : <span className="text-neutral-600">—</span>}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan="8" className="p-8 text-center text-neutral-500">Kayıt bulunamadı.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
